import { InteractionRequiredAuthError } from '@azure/msal-browser';

// Add a Basecamp event to the signed-in person's own Outlook calendar.
// Uses the delegated Graph scope Calendars.ReadWrite through the same MSAL login
// Basecamp already uses — Microsoft asks the person for consent the first time,
// and the event is written to THEIR calendar only (POST /me/events).
// Fallback when consent/Graph isn't available: an .ics file Outlook opens.

const SCOPES = ['Calendars.ReadWrite'];
const STORE_KEY = 'bc-outlook-added';

export function hasDate(ev) { return !!ev?.startsAt; }

function endOf(ev) {
  if (ev.endsAt) return new Date(ev.endsAt);
  return new Date(new Date(ev.startsAt).getTime() + 2 * 3600 * 1000);
}

// Graph wants local wall time + a time zone; UTC keeps it exact.
const graphTime = (d) => ({ dateTime: new Date(d).toISOString().replace('Z', ''), timeZone: 'UTC' });

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

// Remember (per browser) which events the person already added — only for the button label.
export function wasAdded(id) {
  try { return (JSON.parse(localStorage.getItem(STORE_KEY) || '[]')).includes(id); } catch { return false; }
}
function markAdded(id) {
  try {
    const list = JSON.parse(localStorage.getItem(STORE_KEY) || '[]');
    if (!list.includes(id)) localStorage.setItem(STORE_KEY, JSON.stringify([...list, id]));
  } catch { /* storage unavailable — label just won't remember */ }
}

async function getToken(instance) {
  const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
  try {
    if (!account) throw new InteractionRequiredAuthError('no_account');
    const r = await instance.acquireTokenSilent({ scopes: SCOPES, account });
    return r.accessToken;
  } catch (e) {
    if (!(e instanceof InteractionRequiredAuthError) && e?.errorCode !== 'no_account') throw e;
    // First time (or consent revoked): Microsoft shows the consent prompt.
    const r = await instance.acquireTokenPopup({ scopes: SCOPES, account });
    return r.accessToken;
  }
}

// Returns { ok: true } or throws; the caller offers the .ics fallback on error.
export async function addToOutlook(instance, ev) {
  if (!hasDate(ev)) throw new Error('This event has no date yet.');
  const token = await getToken(instance);
  const body = [
    ev.about ? `<p>${escapeHtml(ev.about)}</p>` : '',
    ev.organiser ? `<p>Organiser: ${escapeHtml(ev.organiser)}</p>` : '',
    `<p><a href="${window.location.origin}/news/events">Open in HB Basecamp</a></p>`,
  ].join('');
  const res = await fetch('https://graph.microsoft.com/v1.0/me/events', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      subject: ev.title,
      body: { contentType: 'HTML', content: body },
      start: graphTime(ev.startsAt),
      end: graphTime(endOf(ev)),
      location: { displayName: ev.location || (ev.format === 'online' ? 'Online' : '') },
      isReminderOn: true,
      reminderMinutesBeforeStart: 30,
      categories: ['HB Basecamp'],
      // Same transactionId → Graph doesn't create a duplicate if the person clicks twice.
      transactionId: `hb-basecamp-event-${ev.id}`,
    }),
  });
  if (!res.ok) {
    let msg = `Outlook returned ${res.status}`;
    try { const j = await res.json(); if (j?.error?.message) msg = j.error.message; } catch { /* keep status */ }
    throw new Error(msg);
  }
  markAdded(ev.id);
  return { ok: true };
}

// .ics fallback — opens in Outlook (desktop/web) or any calendar app.
export function downloadIcs(ev) {
  if (!hasDate(ev)) return;
  const fmt = (d) => new Date(d).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const esc = (s) => String(s || '').replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/[,;]/g, m => `\\${m}`);
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Hansen Beck//Basecamp//EN', 'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:hb-basecamp-event-${ev.id}@basecamp.hansenbeck.com`,
    `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(ev.startsAt)}`,
    `DTEND:${fmt(endOf(ev))}`,
    `SUMMARY:${esc(ev.title)}`,
    ev.location || ev.format ? `LOCATION:${esc(ev.location || ev.format)}` : null,
    ev.about ? `DESCRIPTION:${esc(ev.about)}` : null,
    'END:VEVENT', 'END:VCALENDAR',
  ].filter(Boolean).join('\r\n');
  const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(ev.title || 'event').replace(/[^\w\- ]+/g, '').trim() || 'event'}.ics`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Human-readable "Tue 3 Sep 2026, 10:00–12:00" from the start/end, in the viewer's time zone.
export function formatRange(startsAt, endsAt) {
  if (!startsAt) return '';
  const s = new Date(startsAt);
  const e = endsAt ? new Date(endsAt) : null;
  const day = s.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  const t = (d) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (!e) return `${day}, ${t(s)}`;
  return s.toDateString() === e.toDateString()
    ? `${day}, ${t(s)}–${t(e)}`
    : `${day}, ${t(s)} – ${e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, ${t(e)}`;
}
